import React, {ReactElement, useState, useEffect} from 'react';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {ProductVariantsInner, SizesInner} from '@customink/stores-api-client';
import ProductGrid from '../ProductGrid';
import StoreProductDescription from './StoreProductDescription';
import StoreProductColorPicker from '../StoreProductColorPicker';
import StoreProductSizePicker from '../StoreProductSizePicker';
import ProductSizing from '../ProductSizing';
import QuantityStepper from '../QuantityStepper';
import AddToCartDialog from '../AddToCartDialog';
import {useGetStoreProductQuery} from '../../api/storesApi';
import ProductCarousel from '../ProductCarousel';
import FeatureAndDelivery from './FeatureAndDelivery';
import './index.scss';

type Props = {
  storeSlug: string;
  productId: number;
};

const GRID_ORDER = {
  detail: {lg: 1, md: 1, sm: 2, xs: 2},
  carousel: {lg: 2, md: 2, sm: 1, xs: 1},
  featureDelivery: {lg: 3, md: 3, sm: 3, xs: 3}
};

const StoreProductDetail: React.FC<Props> = ({
  storeSlug,
  productId
}: Props): ReactElement => {
  const {data: product} = useGetStoreProductQuery({
    urlSlug: storeSlug,
    productId,
    displayImagesOnly: true
  });
  const [currentVariant, setCurrentVariant] =
    useState<ProductVariantsInner | null>(
      product && product.variants?.length ? product.variants[0] : null
    );
  const [currentSize, setCurrentSize] = useState<SizesInner | null>(
    currentVariant && currentVariant.sizes?.length
      ? currentVariant.sizes[0]
      : null
  );
  const [currentQuantity, setCurrentQuantity] = useState(1);
  const images = currentVariant?.images?.map(item => item.url);

  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (product?.variants && !currentVariant)
      setCurrentVariant(product.variants[0]); // TODO: Support variant selection via URL
  }, [product, currentVariant]);

  return (
    <div className="storeProductDetail">
      <ProductGrid.Container>
        <ProductGrid.Item
          numColLg={1.7}
          numColMd={1}
          numColSm={1}
          order={GRID_ORDER.detail}
          gridKey="detail">
          <StoreProductDescription
            productId={productId}
            storeSlug={storeSlug}
          />
          <StoreProductColorPicker
            currentVariant={currentVariant}
            setCurrentVariant={setCurrentVariant}
            productId={productId}
            storeSlug={storeSlug}
          />
          <StoreProductSizePicker
            currentSize={currentSize}
            setCurrentSize={setCurrentSize}
            currentVariant={currentVariant}
            productId={productId}
            storeSlug={storeSlug}
          />
          <ProductSizing productId={productId} storeSlug={storeSlug} />
          <div
            className={`quantityStepper-container${
              mobileView ? '-mobile' : ''
            }`}>
            <QuantityStepper
              value={currentQuantity}
              onChange={setCurrentQuantity}
              readOnly={false}
            />
            <AddToCartDialog
              productId={productId}
              storeSlug={storeSlug}
              currentVariant={currentVariant}
              currentSize={currentSize}
              currentQuantity={currentQuantity}
              mobile={mobileView}
            />
          </div>
        </ProductGrid.Item>

        <ProductGrid.Item
          numColLg={2.4}
          numColMd={1}
          numColSm={1}
          order={GRID_ORDER.carousel}
          gridKey="carousel">
          <ProductCarousel
            urls={images}
            mobile={mobileView}
            background="gray"
          />
        </ProductGrid.Item>

        <ProductGrid.Item
          order={GRID_ORDER.featureDelivery}
          gridKey="featureDelivery">
          <FeatureAndDelivery productId={productId} storeSlug={storeSlug} />
        </ProductGrid.Item>
      </ProductGrid.Container>
    </div>
  );
};

export default StoreProductDetail;
